import { z } from "zod";
import type { DirectoryMailDto } from "./directoryMail.js";

/**
 * @-vermeldingen in logboeken (issue #154). In de opgeslagen tekst staat een vermelding als `@[Naam](uuid)`;
 * de naam is een momentopname, de uuid is de gebruiker. Zo blijft de tekst leesbaar als platte tekst.
 */
export const MENTION_RE = /@\[([^\]\n]{1,80})\]\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/gi;

/** Genoemde gebruikers (uniek, in volgorde van de tekst). */
export function mentionIds(body: string): string[] {
  return [...new Set([...body.matchAll(MENTION_RE)].map((m) => m[2]!.toLowerCase()))];
}

/**
 * Elke verwijzing in de tekst: een collega `@[Naam](uuid)` (krijgt een melding) of een bedrijf of contact uit het
 * adresboek `@[Naam](company:uuid)` / `@[Naam](person:uuid)` (alleen een link, geen melding).
 */
const REF_RE =
  /@\[([^\]\n]{1,80})\]\((?:(company|person):)?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/gi;

/** Soort verwijzing: collega, of bedrijf of contact uit het adresboek. */
export type MentionKind = "user" | "company" | "person";

export type MentionPart = { text: string } | { mention: { id: string; name: string; kind: MentionKind } };

/** Tekst in stukken voor weergave: gewone tekst en vermeldingen. */
export function mentionParts(body: string): MentionPart[] {
  const out: MentionPart[] = [];
  let last = 0;
  for (const m of body.matchAll(REF_RE)) {
    if (m.index > last) out.push({ text: body.slice(last, m.index) });
    const kind = (m[2]?.toLowerCase() ?? "user") as MentionKind;
    out.push({ mention: { id: m[3]!.toLowerCase(), name: m[1]!, kind } });
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push({ text: body.slice(last) });
  return out;
}

/** Platte tekst met `@Naam` (voor mails en korte weergave). */
export const mentionPlain = (body: string) => body.replace(REF_RE, (_, name: string) => `@${name}`);

/** Link in de app naar een bedrijf of contact uit het adresboek. */
export const directoryPath = (kind: "company" | "person", id: string) =>
  kind === "company" ? `/adresboek/bedrijf/${id}` : `/adresboek?tab=personen&persoon=${id}`;

/**
 * Van wat iemand typte naar opgeslagen tekst: elke `@Naam` van een gekozen persoon wordt `@[Naam](id)`.
 * Langste namen eerst, zodat "@Jan de Vries" niet als "@Jan" wordt gelezen.
 */
export function encodeMentions(text: string, picked: readonly MentionPerson[]): string {
  const people = [...new Map(picked.map((p) => [p.id, p])).values()].sort((a, b) => b.name.length - a.name.length);
  if (!people.length) return text;
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(
    `@\\[[^\\]\\n]*\\]\\([^)]*\\)|(?<![\\p{L}\\p{N}])@(${people.map((p) => esc(p.name)).join("|")})(?![\\p{L}\\p{N}])`,
    "gu",
  );
  return text.replace(re, (whole, name: string | undefined) => {
    if (!name) return whole;
    const p = people.find((x) => x.name === name)!;
    return `@[${p.name.replace(/[\]\n]/g, "")}](${p.kind && p.kind !== "user" ? `${p.kind}:` : ""}${p.id})`;
  });
}

/** Wie je kunt noemen bij een bevinding of melding. */
export interface MentionPerson {
  id: string;
  name: string;
  /** Leeg of `user` = collega; anders een bedrijf of contact uit het adresboek. */
  kind?: MentionKind;
}

/** Entiteiten met een generiek logboek (`log_entries`): meldingen in de Inbox en bedrijven en personen in het adresboek. */
export const LOG_ENTITY_TYPES = ["intake", "company", "person", "document"] as const;
export type LogEntityType = (typeof LOG_ENTITY_TYPES)[number];
export const LOG_ENTRY_KINDS = ["comment"] as const;
export type LogEntryKind = (typeof LOG_ENTRY_KINDS)[number];

/** Waar een vermelding kan staan: het logboek van een bevinding (`issue_events`) of een generiek logboek. */
export const MENTION_SOURCES = ["issue", ...LOG_ENTITY_TYPES] as const;
export type MentionSource = (typeof MENTION_SOURCES)[number];

export const logEntrySchema = z.object({ body: z.string().trim().min(1).max(4000) });

export const mentionPeopleQuery = z.object({ entity: z.enum(MENTION_SOURCES), id: z.uuid() });

export const MENTION_DIRS = ["alle", "aan", "door"] as const;
export type MentionDir = (typeof MENTION_DIRS)[number];
export const mentionListQuery = z.object({ dir: z.enum(MENTION_DIRS).default("alle") });
export const mentionReadSchema = z.object({ ids: z.array(z.uuid()).max(500).optional() });

/** Eén vermelding op Mijn taken › Vermeldingen. */
export interface MentionDto {
  id: string;
  /** De logboekregel. */
  eventId: string;
  source: MentionSource;
  /** Bevinding of melding. */
  targetId: string;
  code: string;
  title: string;
  /** Gebouw van de bevinding of melding; leeg bij logboeken zonder gebouw. */
  building: { id: string; code: string; name: string } | null;
  /** Wie noemde. */
  by: MentionPerson | null;
  /** Wie genoemd is. */
  to: MentionPerson;
  /** Noemde ik in dezelfde regel meer mensen, dan staan de anderen hier. */
  others: MentionPerson[];
  /** Ben ik genoemd (anders noemde ik iemand)? */
  toMe: boolean;
  /** De logboekregel, met `@[Naam](id)`. */
  body: string;
  createdAt: string;
  /** Alleen bij vermeldingen aan mij: wanneer gelezen. */
  readAt: string | null;
}

export interface MentionListResponse {
  items: MentionDto[];
  /** Ongelezen vermeldingen aan mij. */
  unread: number;
}

/**
 * Regel in een logboek. `system` = vaste regel die uit de entiteit zelf volgt (bijv. "Melding binnengekomen");
 * de rest komt uit `log_entries`.
 */
export interface LogEntryDto {
  id: string;
  /** `email_out`/`email_in`: mail uit `directory_mails` in het logboek van een bedrijf of persoon. */
  kind: LogEntryKind | "system" | "email_out" | "email_in";
  body: string;
  user: MentionPerson | null;
  createdAt: string;
  /** Alleen bij `email_out`/`email_in`. */
  mail?: DirectoryMailDto;
}

/** Link in de app naar de bevinding of melding van een vermelding. */
export function mentionPath(m: Pick<MentionDto, "source" | "targetId" | "building">): string {
  switch (m.source) {
    case "issue":
      return `/gebouwen/${m.building?.id ?? ""}?bevinding=${m.targetId}`;
    case "intake":
      return `/inbox?id=${m.targetId}`;
    case "company":
      return `/adresboek/bedrijf/${m.targetId}`;
    case "person":
      return `/adresboek?tab=personen&persoon=${m.targetId}`;
    case "document":
      return `/gebouwen/${m.building?.id ?? ""}/dossier?document=${m.targetId}`;
  }
}
